export type AnswerStep = "sources" | "answer";

function StepBadge({ active, children, style }: { active: boolean; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition-colors ${
        active ? "bg-primary text-primary-foreground" : "border border-border bg-background text-muted-foreground"
      }`}
      style={style}
    >
      {children}
    </span>
  );
}

function StepLabel({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span className={`text-xs font-medium transition-colors ${active ? "text-foreground" : "text-muted-foreground"}`}>
      {children}
    </span>
  );
}

/**
 * The two-step nav between the sources page and the answer page: Neptune's side panel
 * always, Venus once its window is too narrow for both columns side by side.
 */
export function AnswerStepNav({
  current,
  onSelect,
  answerDisabled = false,
  sweepKey = 0,
  labels,
  className = "",
}: {
  /** None while loading: neither step is marked current. */
  current: AnswerStep | null;
  onSelect: (step: AnswerStep) => void;
  /** The answer page has nothing to show yet. */
  answerDisabled?: boolean;
  /** Bumped on each source toggle: replays the sweep over step 1 (keyframes `sourceSweep`,
   * `sourceScalePulseA`/`B` in the host's stylesheet). 0 plays nothing. */
  sweepKey?: number;
  labels: { ariaLabel: string; sources: string; answer: string };
  className?: string;
}) {
  const onSources = current === "sources";
  const onAnswer = current === "answer";
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-label={labels.ariaLabel}>
      <button
        type="button"
        onClick={() => onSelect("sources")}
        aria-current={onSources ? "step" : undefined}
        className="relative flex cursor-pointer items-center gap-1.5 overflow-hidden rounded px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {/* Sweep shimmer overlay — replays on each source toggle via key remount */}
        {sweepKey > 0 && (
          <span
            key={sweepKey}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded"
            style={{
              background: "linear-gradient(90deg, transparent, color-mix(in srgb, #345AEF 18%, transparent), transparent)",
              animation: "sourceSweep 0.5s ease-out forwards",
            }}
          />
        )}
        <StepBadge
          active={onSources}
          style={
            sweepKey > 0
              ? {
                  animationName: sweepKey % 2 === 0 ? "sourceScalePulseA" : "sourceScalePulseB",
                  animationDuration: "0.45s",
                  animationTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  animationFillMode: "forwards",
                }
              : undefined
          }
        >
          1.
        </StepBadge>
        <StepLabel active={onSources}>{labels.sources}</StepLabel>
      </button>
      <div aria-hidden="true" className={`h-px w-12 transition-colors ${onAnswer ? "bg-primary" : "bg-border"}`} />
      <button
        type="button"
        onClick={() => onSelect("answer")}
        disabled={answerDisabled}
        aria-current={onAnswer ? "step" : undefined}
        className="flex cursor-pointer items-center gap-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-default disabled:opacity-50"
      >
        <StepBadge active={onAnswer}>2.</StepBadge>
        <StepLabel active={onAnswer}>{labels.answer}</StepLabel>
      </button>
    </div>
  );
}
